import * as angular from 'angular';
import * as moment from 'moment';

import { DateRange, IDateRange } from '../../models/dateRange';
import { TimeUnit } from '../../models/enum';
import { momentFormat } from '../../common/constants';
import { IDateAlignmentHelperService } from './';
import { IAuthenticationService } from '../general';
import { IUserService, ICurrentApplicationService } from '../data';
import { MomentHelper } from '../../common/';

export interface IDateNavigationService {
    getKeywordDates(timeUnit?: TimeUnit, isLimited?: boolean): angular.IPromise<DateRange[]>;
    getToday(): angular.IPromise<DateRange>;
    getYesterday(): angular.IPromise<DateRange>;
    getLast24h(): angular.IPromise<DateRange>;
    getLast7Days(): angular.IPromise<DateRange>;
    getThisWeek(): angular.IPromise<DateRange>;
    getLastWeek(): angular.IPromise<DateRange>;
    getLastMonth(): angular.IPromise<DateRange>;
    getWeekToDate(): angular.IPromise<DateRange>;
    getMonthToDate(): angular.IPromise<DateRange>;
    getLast30Days(): angular.IPromise<DateRange>;
    getThisMonth(): angular.IPromise<DateRange>;
    getLast12Months(): angular.IPromise<DateRange>;
    getLast3Months(): angular.IPromise<DateRange>;
    getLastQuarter(): angular.IPromise<DateRange>;
    getLastYear(): angular.IPromise<DateRange>;
    getNextMonth(): angular.IPromise<DateRange>;
    getNextQuarter(): angular.IPromise<DateRange>;
    getNextYear(): angular.IPromise<DateRange>;
    getQuarterToDate(): angular.IPromise<DateRange>;
    getThisQuarter(): angular.IPromise<DateRange>;
    getThisYear(): angular.IPromise<DateRange>;
    getYearToDate(): angular.IPromise<DateRange>;
    getLastRange(): angular.IPromise<DateRange>;
    getLastRanges(): angular.IPromise<DateRange[]>;
    getDelay(interval: number, unit: TimeUnit): number;
    getLastNPeriod(n: number): IDateRange;
    getPreviousAtN(input: moment.Moment, n: number): moment.Moment;
    getNextAtN(input: moment.Moment, n: number): moment.Moment;
}

export class DateNavigationService implements IDateNavigationService {
    static serviceId = 'dateNavigationService';

    private formatMoment = momentFormat;

    static $inject = [
        '$q',
        'currentApplicationService',
        'dateAlignmentHelperService',
        'userService',
        'authenticationService'
    ];

    constructor(
        private $q: angular.IQService,
        private _currentApplicationService: ICurrentApplicationService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _userService: IUserService,
        private _authenticationService: IAuthenticationService,
    ) {
    }

    getKeywordDates(timeUnit?: TimeUnit, isLimited?: boolean): angular.IPromise<DateRange[]> {
        let promises = [];

        if (!isLimited) {
            const lessThanYear = timeUnit < TimeUnit.Year;
            if (lessThanYear) {
                promises = [
                    this.getLast3Months(),
                    this.getThisQuarter(),
                    this.getQuarterToDate(),
                    this.getLast12Months()
                ];
            }

            promises.push(this.getThisYear());

            if (lessThanYear) {
                promises.push(this.getYearToDate());
            }
        }

        if (timeUnit <= TimeUnit.Month) {
            promises.unshift(...[
                this.getLastDays(30),
                this.getThisMonth(),
                this.getMonthToDate()
            ]);
        }

        if (timeUnit <= TimeUnit.Week) {
            promises.unshift(...[
                this.getLastDays(7),
                this.getThisWeek(),
                this.getWeekToDate()
            ]);
        }

        if (timeUnit <= TimeUnit.Day) {
            promises.unshift(this.getYesterday());
        }

        if (timeUnit < TimeUnit.Day) {
            promises.unshift(this.getLast24h());
        }

        if (timeUnit <= TimeUnit.Day) {
            promises.unshift(this.getToday());
        }

        return this.$q.all(promises);
    }

    getToday(): angular.IPromise<DateRange> {
        const beginMoment = this.getStartOfDay();
        const endMoment = this.getEndOfDay();
        const promises = [
            this._dateAlignmentHelperService.getNextForAggregation(beginMoment, 1, TimeUnit.Day),
            this._dateAlignmentHelperService.getNextForAggregation(endMoment, 1, TimeUnit.Day)
        ];
        return this.$q.all(promises)
            .then(data => this.alignRange(data[0], data[1], 'Today'));
    }

    getYesterday(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const beginMoment = this.getStartOfDay().add(-1, 'd');
        const endMoment = this.getEndOfDay().add(-1, 'd');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'Yesterday'))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getLast24h(): angular.IPromise<DateRange> {
        const endMoment = moment().startOf('h');
        const beginMoment = endMoment.clone().subtract(1, 'd');
        return this.alignRange(beginMoment, endMoment, 'Last 24h');
    }

    getLastDays(value: number): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const endMoment = this.getStartOfDay();
        const beginMoment = endMoment.clone().subtract(value, 'd');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], value === 1 ? `Last day` : `Last ${value} days`))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getLastMonths(value: number): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const endMoment = moment().startOf('M');
        const beginMoment = endMoment.clone().subtract(value, 'M');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], value === 1 ? `Last month` : `Last ${value} months`))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getLastWeek(): angular.IPromise<DateRange> {
        let endMoment = this.getStartOfDay();
        return this._dateAlignmentHelperService
            .getStartOfWeek(endMoment)
            .then(end => {
                endMoment = end;
                const beginMoment = end.clone().subtract(1, 'w');
                return this.alignBeginEnd(beginMoment, endMoment);
            })
            .then(data => this.alignRange(data[0], data[1], 'Last week'));
    }

    getThisWeek(): angular.IPromise<DateRange> {
        return this._dateAlignmentHelperService
            .getStartOfWeek(this.getStartOfDay())
            .then(begin => this.alignBeginEnd(begin, begin.clone().add(1, 'w')))
            .then(data => this.alignRange(data[0], data[1], 'This week'));
    }

    getWeekToDate(): angular.IPromise<DateRange> {
        let endMoment = this.getStartOfDay();
        return this._dateAlignmentHelperService
            .getStartOfWeek(endMoment)
            .then(firstDayOfWeek => {
                let beginMoment;
                if (endMoment.days() === firstDayOfWeek.days()) {
                    endMoment = firstDayOfWeek.startOf('d');
                    beginMoment = endMoment.clone().subtract(7, 'd');
                } else {
                    beginMoment = firstDayOfWeek;
                }
                return this.alignBeginEnd(beginMoment, endMoment);
            })
            .then(data => this.alignRange(data[0], data[1], 'Week to date'));
    }

    getLastMonth(): angular.IPromise<DateRange> {
        return this.getLastMonths(1);
    }

    getThisMonth(): angular.IPromise<DateRange> {
        const beginMoment = moment().startOf('M');
        const endMoment = beginMoment.clone().add(1, 'M');
        return this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'This month'));
    }

    getMonthToDate(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const isFirstDayOfPeriod = this.isFirstDayOfPeriod('M');
        let endMoment = this.getStartOfDay();
        let beginMoment = endMoment.clone().startOf('M');
        if (isFirstDayOfPeriod) {
            endMoment = moment().startOf('M');
            beginMoment = endMoment.clone().subtract(1, 'M');
        }
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'Month to date'))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getLast7Days(): angular.IPromise<DateRange> {
        return this.getLastDays(7);
    }

    getLast30Days(): angular.IPromise<DateRange> {
        return this.getLastDays(30);
    }

    getLast12Months(): angular.IPromise<DateRange> {
        return this.getLastMonths(12);
    }

    getLast3Months(): angular.IPromise<DateRange> {
        return this.getLastMonths(3);
    }

    getLastQuarter(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const endMoment = moment().startOf('Q');
        const beginMoment = endMoment.clone().subtract(1, 'Q');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], `Last quarter`))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getLastYear(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const endMoment = moment().startOf('y');
        const beginMoment = endMoment.clone().subtract(1, 'y');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], `Last year`))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getNextMonth(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const beginMoment = moment().startOf('M').add(1, 'M');
        const endMoment = beginMoment.clone().add(1, 'M');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'Next month'))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getNextQuarter(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const beginMoment = moment().startOf('Q').add(1, 'Q');
        const endMoment = beginMoment.clone().add(1, 'Q');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], `Next quarter`))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getNextYear(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const beginMoment = moment().startOf('y').add(1, 'y');
        const endMoment = beginMoment.clone().add(1, 'y');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], `Next year`))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getQuarterToDate(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const isFirstDayOfPeriod = this.isFirstDayOfPeriod('Q');
        let endMoment = this.getStartOfDay();
        let beginMoment = endMoment.clone().startOf('Q');
        if (isFirstDayOfPeriod) {
            endMoment = moment().startOf('Q');
            beginMoment = endMoment.clone().subtract(1, 'Q');
        }
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'Quarter to date'))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getThisQuarter(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const beginMoment = moment().startOf('Q');
        const endMoment = beginMoment.clone().add(1, 'Q');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'This quarter'))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getThisYear(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const beginMoment = moment().startOf('y');
        const endMoment = beginMoment.clone().add(1, 'y');
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'This year'))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getYearToDate(): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const isFirstDayOfPeriod = this.isFirstDayOfPeriod('y');
        let endMoment = this.getStartOfDay();
        let beginMoment = endMoment.clone().startOf('y');
        if (isFirstDayOfPeriod) {
            endMoment = moment().startOf('y');
            beginMoment = endMoment.clone().subtract(1, 'y');
        }
        this.alignBeginEnd(beginMoment, endMoment)
            .then(data => this.alignRange(data[0], data[1], 'Year to date'))
            .then(range => defered.resolve(range));
        return defered.promise;
    }

    getLastRange(): angular.IPromise<DateRange> {
        const deferred = this.$q.defer<DateRange>();
        this.getLastRanges()
            .then(data => {
                let range: DateRange;
                if (data && data.length > 0) {
                    range = data[0];
                    range.beginDateTime = this.getLocalMoment(range.beginDateTime, range.timeZone)
                        .format(this.formatMoment);
                    range.endDateTime =
                        this.getLocalMoment(range.endDateTime, range.timeZone).format(this.formatMoment);
                } else {
                    range = {
                        beginDateTime: moment().subtract(7, 'd').format(this.formatMoment),
                        endDateTime: moment().format(this.formatMoment)
                    } as any;
                }
                deferred.resolve(range);
            })
            .catch(() => deferred.reject());
        return deferred.promise;
    }

    getLastRanges(): angular.IPromise<DateRange[]> {
        const deferred = this.$q.defer<DateRange[]>();

        this._userService.getSelectedDatesForUser(this._authenticationService.authentication.userName)
            .then(data => deferred.resolve(data.selectedDates))
            .catch(() => deferred.reject());
        return deferred.promise;
    }

    private isFirstDayOfPeriod(period: 'M' | 'Q' | 'y'): boolean {
        const now = moment();
        const dayOfMonth = now.date();
        const monthNumber = now.month();
        let isFirstDayOfPeriod = false;
        switch (period) {
            case 'M':
                isFirstDayOfPeriod = dayOfMonth === 1;
                break;
            case 'Q':
                isFirstDayOfPeriod = dayOfMonth === 1 && (monthNumber % 3 === 0); // Months are 0-based in moment, so 0===January
                break;
            case 'y':
                isFirstDayOfPeriod = dayOfMonth === 1 && monthNumber === 0;
                break;
        }
        return isFirstDayOfPeriod;
    }

    private alignBeginEnd(beginMoment: moment.Moment, endMoment: moment.Moment): angular.IPromise<[moment.Moment, moment.Moment]> {
        const defered = this.$q.defer<[moment.Moment, moment.Moment]>();
        const promises: [angular.IPromise<moment.Moment>, angular.IPromise<moment.Moment>] = [
            this._dateAlignmentHelperService.getNextForAggregation(beginMoment, 1, TimeUnit.Day),
            this._dateAlignmentHelperService.getNextForAggregation(endMoment, 1, TimeUnit.Day)
        ];
        this.$q.all(promises)
            .then((data: [moment.Moment, moment.Moment]) => {
                [beginMoment, endMoment] = data;
                defered.resolve(data);
            });
        return defered.promise;
    }

    private alignRange(
        beginMoment: moment.Moment,
        endMoment: moment.Moment,
        keyword: string
    ): angular.IPromise<DateRange> {
        const defered = this.$q.defer<DateRange>();
        const dateRange = new DateRange();
        dateRange.keyword = keyword;

        const promises = [
            this._dateAlignmentHelperService.getNext(beginMoment),
            this._dateAlignmentHelperService.getNext(endMoment)
        ];
        this.$q.all(promises)
            .then((responses: moment.Moment[]) => {
                dateRange.beginDateTime = this.localFormat(responses[0]);
                dateRange.endDateTime = this.localFormat(responses[1]);
                defered.resolve(dateRange);
            })
            .catch(() => defered.reject());
        return defered.promise;
    }

    private getLocalMoment(date: string, timeZone: string): moment.Moment {
        return moment(moment.tz(date, timeZone).toDate());
    }

    private localFormat(input: moment.Moment): string {
        return moment(input.toISOString()).format(this.formatMoment);
    }

    getDelay(interval: number, unit: TimeUnit): number {
        let coeff = 1;

        switch (unit) {
            case TimeUnit.Month:
                coeff *= 30;
            case TimeUnit.Week:
                coeff *= 7;
            case TimeUnit.Day:
                coeff *= 24;
            case TimeUnit.Hour:
                coeff *= 60;
            case TimeUnit.Minute:
                coeff *= 60;
            case TimeUnit.Second:
                coeff *= 1000;
        }

        return interval * coeff;
    }

    getLastNPeriod(n: number): IDateRange {
        const config = this._currentApplicationService.getApplicationConfiguration();
        const endDate = this._dateAlignmentHelperService.getPreviousSync(moment(), config);
        const beginDate = this.getPreviousAtN(endDate, n);
        return { beginDate, endDate };
    }

    getPreviousAtN(input: moment.Moment, n: number): moment.Moment {
        const config = this._currentApplicationService.getApplicationConfiguration();
        const { interval, intervalTimeUnit } = config;
        const duration = moment.duration(n * interval, MomentHelper.getPrefix(intervalTimeUnit));
        const beginDate: moment.Moment = this._dateAlignmentHelperService.getPreviousSync(input.clone().subtract(duration), config);
        return beginDate;
    }

    getNextAtN(input: moment.Moment, n: number): moment.Moment {
        const config = this._currentApplicationService.getApplicationConfiguration();
        const { interval, intervalTimeUnit } = config;
        const duration = moment.duration(n * interval, MomentHelper.getPrefix(intervalTimeUnit));
        const beginDate: moment.Moment = this._dateAlignmentHelperService.getNextSync(input.clone().add(duration), config);
        return beginDate;
    }

    private getStartOfDay(): moment.Moment {
        const result = moment().startOf('d').millisecond(0);
        return this.isBeforeBeginDayTime()
            ? result.add(-1, 'd')
            : result;
    }

    // This produce a 23:59:59 result, but's it's to mimic the date selection on picker
    private getEndOfDay(): moment.Moment {
        const result = moment().endOf('d').millisecond(0);
        return this.isBeforeBeginDayTime()
            ? result.add(-1, 'd')
            : result;
    }

    private isBeforeBeginDayTime(): boolean {
        const config = this._currentApplicationService.getApplicationConfiguration();
        return moment().startOf('d').add(config.beginDayTime).isAfter(moment());
    }
}